<template>
  <v-container fluid fill-height ma-0 px-0 py-1>
    <v-layout column fill-height pa-0>
      <loading-circle
        :show="loader"
      />
        <v-flex shrink pa-0>
          <v-layout pa-0 class="white">
            <v-flex xs3 pa-0>
              <v-select
                :data-testid="$options.name + '__select__' + 'event type'"
                v-model="filter"
                light
                :items="getItems.type"
                :label="$t('event type')"
                :menu-props="{ maxHeight: '400' }"
                solo
                hide-details
                dense
                flat
                clearable
                multiple
              >
                <template v-slot:item="{ item, index }">
                  <span>{{ $t(String(item)) }}</span>
                </template>
                <template v-slot:selection="{ item, index }">
                  <span v-if="filter.length > 1">{{ $t(String(item)) }} / </span>
                  <span v-else>{{ $t(String(item)) }}</span>
                </template>
              </v-select>
            </v-flex>

            <v-flex xs3 pa-0>
              <v-select
                :data-testid="$options.name + '__select__' + 'filterState'"
                v-model="filterState"
                light
                :items="getItems.state"
                :label="$t('state')"
                :menu-props="{ maxHeight: '400' }"
                solo
                hide-details
                dense
                flat
                clearable
                multiple
              >
                <template v-slot:item="{ item, index }">
                  <span>{{ $t(String(item)) }}</span>
                </template>
                <template v-slot:selection="{ item, index }">
                  <span v-if="filterState.length > 1">{{ $t(String(item)) }} / </span>
                  <span v-else>{{ $t(String(item)) }}</span>
                </template>
              </v-select>
            </v-flex>
            <v-flex xs5 pa-0>
              <v-text-field
                :data-testid="$options.name + '__text-field__' + 'search'"
                v-model.sync="search"
                append-icon="search"
                solo
                flat
                light
                :label="$t('search')"
                :placeholder="$t('enter a search term')"
                single-line
                hide-details
              ></v-text-field>
            </v-flex>
            <v-flex shrink pa-0>
              <v-tooltip dark color="secondary" bottom>
                <v-btn small icon color="secondary" slot="activator" dark text 
                v-if="$store.state.auth.user.level <= 1"
                :data-testid="$options.name + '__btn__' + 'createEvent'"
                @click="createEvent"
                >
                  <v-icon color="white">add</v-icon>
                </v-btn>
                <span>{{ $t('create new') }}</span>
              </v-tooltip>
            </v-flex>
            <v-flex pa-0>
              <v-tooltip dark color="secondary" bottom>
                <v-btn small block color="secondary" slot="activator" dark text 
                :data-testid="$options.name + '__btn__' + 'generatePDF'"
                @click="generatePDF">{{$t('PDF')}}</v-btn>
                <span>{{ $t('download PDF') }}</span>
              </v-tooltip>
            </v-flex>
            <v-flex pl-2>
              <v-tooltip dark color="secondary" bottom>
                <v-btn small block color="secondary" slot="activator" dark text 
                :data-testid="$options.name + '__btn__' + 'csvExport'"
                @click="csvExport">{{$t('CSV')}}</v-btn>
                <span>{{ $t('download CSV') }}</span>
              </v-tooltip>
            </v-flex>
          </v-layout>
        </v-flex>

      <v-flex xs12 pa-0>
        <v-layout column fill-height justify-start ma-0 px-0 py-1>
          <v-flex style="position: relative">
            <v-layout fill-height ref="events-table" >

              <scroll-table
                :data="getTable"
                :options="getOptions"
                :sortable="false"
                :translate="true"
                :sorted="sort"
                :headUppercase="false"
                @select="(row) => showEvent(row)"
                @sort="(item) => sortTable(item)"
              />

            </v-layout>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script lang="ts">
import Vue from 'vue'
import loadingCircle from '@/nms/components/partials/loader.vue'
import scrollTable from '@/nms/components/partials/scrolltable.vue'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'
import myFont from '@/nms/assets/RobotoCondensed-Regular-normal.js'

// Типы
interface TableColumn {
  title: string
  value: string
  dataKey: string
  type: string
  width: string
}

interface TableRow {
  state?: string
  last_update_user?: string
  rt?: string | Date
  type?: string
  users_name?: string
  name?: string
  $id?: string
  [key: string]: any
}

interface TableData {
  columns: TableColumn[]
  rows: TableRow[]
}

interface OptionsColumns {
  [key: string]: { width: string }
}

interface OptionsData {
  columns: OptionsColumns
}

interface ItemsData {
  type: string[]
  state: string[]
}

interface LocationData {
  $id?: string
  cls?: string
  type?: string
  name?: string
  [key: string]: any
}

interface CmdbQueryResponse {
  $id?: string
  [key: string]: any
}

interface DialogData {
  component: string
  parentActions: {
    setup: () => void
  }
  id: string
  data: any
  options: {
    width: number
    height: number
    top: number
    left: number
  }
}

interface EventItem {
  $id?: string
  type?: string
  state?: string
  rt?: string | Date
  [key: string]: any
}

export default Vue.extend({
  name: 'EVENTS_TABLE',

  components: {
    loadingCircle,
    scrollTable,
  },

  props: {
    data: {
      type: Object as () => LocationData,
      default: () => ({})
    },
    devices: {
      type: Array as () => any[],
      default: () => []
    },
  },

  data(): {
    loader: boolean
    options: OptionsData
    filter: any[]
    filterState: string[]
    search: string
    sort: string
    items: ItemsData
    table: TableData
    statuses?: Record<string, string>
  } {
    return {
      loader: false,
      options: {
        columns: {
          text: { width: '40%' },
          timestamp: { width: '40%' },
          actions: { width: '40%' },
          endpoint: { width: '40%' },
          check: { width: '20%' },
        },
      },
      filter: [],
      filterState: ['new_event', 'read_event'],
      search: '',
      sort: '-rt',
      items: { type: [], state: [] },
      table: {
        columns: [
          {
            title: 'state',
            value: 'state',
            dataKey: 'state',
            type: 'text',
            width: '20%'
          },
          {
            title: 'author',
            value: 'last_update_user',
            dataKey: 'last_update_user',
            type: 'text',
            width: '15%'
          },
          {
            title: 'date',
            value: 'rt',
            dataKey: 'rt',
            type: 'timestamp',
            width: '8%'
          },
          {
            title: 'type',
            value: 'type',
            dataKey: 'type',
            type: 'text',
            width: '20%'
          },
          {
            title: 'name',
            value: 'users_name',
            dataKey: 'users_name',
            type: 'text',
            width: '20%'
          },
        ],
        rows: []
      },
    }
  },

  computed: {
    getTable(): TableData {
      const t = this.$merge({}, this.table) as TableData

      if (t.rows && this.filter.length) {
        t.rows = t.rows.filter(row => this.filter.includes(row.type || ''))
      }
      if (t.rows && this.filterState.length) {
        t.rows = t.rows.filter(row => this.filterState.includes(row.state || ''))
      }

      if (this.search.length) {
        const searchLower = this.search.toLowerCase()
        t.rows = t.rows.filter(row => {
          return (row.users_name && row.users_name.toLowerCase().indexOf(searchLower) > -1) ||
                 (row.type && row.type.toLowerCase().indexOf(searchLower) > -1) ||
                 (row.name && row.name.toLowerCase().indexOf(searchLower) > -1)
        })
      }

      if (this.sort) {
        const isDescending = this.sort.charAt(0) === '-'
        const fieldName = isDescending ? this.sort.slice(1) : this.sort
        const direction = isDescending ? -1 : 1
        t.rows = [...t.rows].sort(this.$compare(fieldName, direction))
      }

      return t
    },

    getOptions(): OptionsData {
      return this.options
    },

    getItems(): ItemsData {
      return this.items
    },

    getData(): any {
      return this.data
    },
  },

  watch: {
    data: {
      handler(): void {
        this.setup()
      },
      deep: true
    },
  },

  created(): void {
    this.setup()
  },

  methods: {
    setup(): void {
      this.reset()
      this.prepareTable()
    },

    async createEvent(): Promise<void> {
      try {
        console.log(this.$options.name, 'createEvent', 'this.data', this.data)
        const query = {
          id: [this.data.$id],
          notNull: true,
          data: { namePath: true }
        }
        const obj = await this.$bridge.cmdbQuery(query) as CmdbQueryResponse[]

        if (!obj?.length) return
        const obj$ = obj[0]

        if (obj$) {
          const dialogData: DialogData = {
            component: 'eventModal',
            parentActions: { setup: this.setup },
            id: this.data.$id || '',
            data: obj$,
            options: {
              width: (this.$root.$el as HTMLElement).clientWidth * 0.4,
              height: (this.$root.$el as HTMLElement).clientHeight * 0.6,
              top: 100,
              left: 200
            }
          }
          this.$store.dispatch('setDialog', dialogData)
        }
      } catch (error) {
        console.error('CATCH', this.$options.name, 'createEvent', 'error', error)
      }
    },

    async showEvent(event: EventItem): Promise<void> {
      console.log(this.$options.name, 'showEvent', 'event', event)
      try {
        const obj$ = await this.$bridge.getObjectById(event.$id)
        console.log(this.$options.name, 'setDialog obj$', obj$)

        if (obj$) {
          const dialogData: DialogData = {
            component: 'eventModal',
            parentActions: { setup: this.setup },
            id: event.$id || '',
            data: event,
            options: {
              width: (this.$root.$el as HTMLElement).clientWidth * 0.4,
              height: (this.$root.$el as HTMLElement).clientHeight * 0.6,
              top: 100,
              left: 200
            }
          }
          this.$store.dispatch('setDialog', dialogData)
        }
      } catch (error) {
        console.error('CATCH', this.$options.name, 'showEvent', 'error', error)
      }
    },

    sortTable(item: TableColumn): void {
      if (this.sort === item.value) {
        this.sort = '-' + item.value
      } else {
        this.sort = item.value
      }
    },

    async prepareTable(): Promise<void> {
      try {
        console.log(this.$options.name, 'prepareTable', 'this.data', this.data)

        const locationQuery = {
          id: [this.data.$id],
          notNull: true,
          data: { links: { flatten: false } }
        }
        const locationTree = await this.$bridge.cmdbQuery(locationQuery)
        console.log(this.$options.name, 'prepareTable', 'locationTree', locationTree)

        const eventQuery = {
          id: [this.data.$id],
          notNull: true,
          data: this.data.cls === 'location' ? {} : { links: { flatten: true } },
          reducer: 'events'
        }

        const events = await this.$bridge.cmdbQuery(eventQuery) as EventItem[]

        this.$set(this.items, 'type', [])
        this.$set(this.items, 'state', [])

        events?.forEach(event => {
          if (event.type && !this.items.type.includes(event.type)) {
            this.items.type.push(event.type)
          }
          if (event.state && !this.items.state.includes(event.state)) {
            this.items.state.push(event.state)
          }
        })

        this.table.rows = events?.sort(this.$compare('$id', -1)) || []
        this.loader = false
      } catch (err) {
        console.error('CATCH', this.$options.name, 'prepareTable', err)
        this.loader = false
      }
    },

    async statusSubscribe(arr: any[], group: string): Promise<void> {
      this.$store.dispatch('measuresSubscribeInit', {
        name: 'addStatus',
        handler: this.subscribeHandler,
        group: group,
        listener: 'status'
      })

      if (arr.length) {
        arr.forEach(item => {
          if (item.$id) {
            this.$store.dispatch('addStatusKey', item)
          }
        })
      }
      await this.$bridge.subscribe(arr.map(obj => obj.$id))
    },

    subscribeHandler(e: { $id?: string; [key: string]: any }): void {
      if (e.$id) {
        this.$store.dispatch('addStatus', e)
      }
    },

    statusUnsubscribe(group: string): void {
      this.$store.dispatch('measuresSubscribeClose', {
        handler: this.subscribeHandler,
        group: group,
        listener: 'status'
      })
    },

    generatePDF(): void {
      this.$generatePDF(
        this.getTable,  // объект таблицы с rows и columns
        this.getData,       // или объект вида {cls: String || '', type: String || '', name: String || ''}
        null,           // caller (опционально)
        {
          orientation: 'landscape',  // альбомная ориентация
          format: 'a4',              // формат A4
          font: 'MyFont'             // кастомный шрифт
        }
      )
      return
    },

    csvExport(): void {
      this.$csvExport(this.getTable, this.$options.name, this.$options.name)
      return
    },

    getFormattedDate(rows: TableRow[]): TableRow[] {
      const result: TableRow[] = []
      rows.forEach(row => {
        const newRow: TableRow = {}
        Object.keys(row).forEach(key => {
          const column = this.table.columns.find(col => col.value === key)
          if (column?.type === 'timestamp' && row[key]) {
            newRow[key] = (this as any).$moment(row[key]).format('DD MM YYYY HH:mm')
          } else {
            newRow[key] = this.$getTranslate(String(row[key]))
          }
        })
        result.push(newRow)
      })
      return result
    },

    reset(): void {
      this.table.rows = []
      this.statuses = {}
    },
  },
})
</script>

<style>
</style>
